import type { Venue } from '../types';

const THERAPY_LABELS: Record<string, string> = {
  sauna: 'Sauna',
  'ice-bath': 'Cold plunge',
  'contrast-therapy': 'Contrast therapy',
  cryotherapy: 'Cryotherapy',
  hbot: 'HBOT',
  'red-light': 'Red light',
  'float-therapy': 'Float therapy',
  'iv-therapy': 'IV therapy',
};

function formatOfferValue(value: string): string {
  const normalized = value
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (!normalized) return '';

  return normalized
    .split(' ')
    .map((word) => {
      const lower = word.toLowerCase();
      if (lower === 'hbot') return 'HBOT';
      if (lower === 'iv') return 'IV';
      if (lower === 'led') return 'LED';
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(' ');
}

function pushUniqueLabel(target: string[], value?: string | null) {
  const formatted = value ? formatOfferValue(value) : '';
  if (!formatted) return;
  const exists = target.some((entry) => entry.toLowerCase() === formatted.toLowerCase());
  if (!exists) {
    target.push(formatted);
  }
}

export function formatFacilityType(value?: string | null): string | null {
  const formatted = value?.replace(/\s+/g, ' ').trim();
  return formatted ? formatted : null;
}

export function getVenueBestForLabel(
  value?: string | null,
  facilityType?: string | null,
  primaryTherapy?: string | null,
): string | null {
  const formatted = value?.replace(/\s+/g, ' ').trim();
  if (formatted) {
    const normalised = formatted.toLowerCase();
    if (normalised !== 'best for recovery') return formatted;
  }

  const facility = facilityType?.toLowerCase() ?? '';
  if (facility.includes('outdoor')) return 'Outdoor recovery';
  if (facility.includes('medical')) return 'Clinical recovery';
  if (facility.includes('clinic')) return 'Clinical recovery';
  if (facility.includes('gym')) return 'Training-led recovery';
  if (facility.includes('dedicated wellness studio')) return 'Private recovery sessions';
  if (facility.includes('wellness studio')) return 'Focused recovery sessions';

  const therapy = primaryTherapy?.toLowerCase() ?? '';
  if (therapy.includes('sauna')) return 'Heat-first recovery';
  if (therapy.includes('ice')) return 'Cold exposure';
  if (therapy.includes('cryo')) return 'Fast cold recovery';
  if (therapy.includes('hbot')) return 'Deep recovery support';
  if (therapy.includes('red')) return 'Lower-friction recovery';
  if (therapy.includes('contrast')) return 'Hot-cold recovery';

  return null;
}

export function getVenueAmenitySignals(
  venue: Pick<
    Venue,
    'privateSessionsAvailable' | 'membershipAvailable' | 'wheelchairAccessible' | 'parkingAvailable'
  >,
  max = 3,
): string[] {
  const signals = [
    venue.privateSessionsAvailable ? 'Private sessions' : null,
    venue.membershipAvailable ? 'Membership' : null,
    venue.wheelchairAccessible ? 'Accessible' : null,
    venue.parkingAvailable ? 'Parking' : null,
  ].filter(Boolean) as string[];

  return signals.slice(0, max);
}

export function getVenueOfferHighlights(
  venue: Pick<Venue, 'therapy' | 'primaryTherapy' | 'saunaTypes' | 'features'>,
  max = 3,
): string[] {
  const offers: string[] = [];
  const saunaTypes = venue.saunaTypes ?? [];

  saunaTypes.forEach((type) => {
    const normalized = type?.trim().toLowerCase();
    if (!normalized) return;
    const label = normalized.includes('sauna') ? type : `${type} sauna`;
    pushUniqueLabel(offers, label);
  });

  const therapies = [venue.primaryTherapy, ...(venue.therapy ?? [])].filter(Boolean) as string[];
  therapies.forEach((therapy) => {
    const slug = therapy.toLowerCase();
    if (slug === 'sauna' && saunaTypes.length > 0) return;
    pushUniqueLabel(offers, THERAPY_LABELS[slug] ?? therapy);
  });

  const featureText = (venue.features ?? []).join(' ').toLowerCase();
  const featureOfferMatches: Array<[string, string]> = [
    ['infrared sauna', 'Infrared sauna'],
    ['finnish sauna', 'Finnish sauna'],
    ['wood-fired sauna', 'Wood-fired sauna'],
    ['steam room', 'Steam room'],
    ['cold plunge', 'Cold plunge'],
    ['ice bath', 'Ice bath'],
    ['contrast therapy', 'Contrast therapy'],
    ['cryotherapy', 'Cryotherapy'],
    ['hyperbaric oxygen', 'HBOT'],
    ['red light', 'Red light'],
    ['float tank', 'Float therapy'],
    ['floatation', 'Float therapy'],
  ];

  featureOfferMatches.forEach(([pattern, label]) => {
    if (featureText.includes(pattern)) {
      pushUniqueLabel(offers, label);
    }
  });

  return offers.slice(0, max);
}

export function getVenueReviewHighlight(
  venue: Pick<Venue, 'reviewSummary' | 'reviewSnippets'>,
): string | null {
  const reviewText = venue.reviewSummary?.trim() || venue.reviewSnippets?.find((snippet) => snippet?.trim())?.trim();
  return reviewText ? reviewText : null;
}

export function getVenuePriceSignal(priceRange?: string | null): string {
  const value = priceRange?.trim();
  if (!value) return 'Price on request';

  const pounds = (value.match(/£/g) || []).length;
  if (pounds === 1) return `${value} · Budget`;
  if (pounds === 2) return `${value} · Mid-range`;
  if (pounds === 3) return `${value} · Premium`;
  if (pounds >= 4) return `${value} · Luxury`;

  return value;
}

export function getVenueLocationDisplay(
  venue: Pick<Venue, 'area' | 'neighborhood' | 'county' | 'city'>,
): string {
  const neighborhood = venue.neighborhood?.trim();
  const area = venue.area?.trim();
  const county = venue.county?.trim();
  const city = venue.city?.trim();
  const normalizedCounty = county?.toLowerCase();

  if (neighborhood && county && normalizedCounty && !normalizedCounty.includes('london')) {
    return `${neighborhood} · ${county}`;
  }

  if (neighborhood && area && neighborhood.toLowerCase() !== area.toLowerCase()) {
    return `${neighborhood} · ${area}`;
  }

  if (neighborhood) return neighborhood;

  if (area && county && normalizedCounty && !normalizedCounty.includes('london') && area.toLowerCase() !== county.toLowerCase()) {
    return `${area} · ${county}`;
  }

  return area || county || city || 'Location available on profile';
}
